/**
 * BackToTopButton — subtle floating "↑" button that fades in after the
 * user scrolls past a threshold (default 600px). Clicks smooth-scroll the
 * window back to top.
 *
 * Positioning: sits ABOVE the AIChatWidget (bottom-left stack) so it never
 * collides with WhatsApp / Share FAB on the right.
 *
 * Accessibility: respects ``prefers-reduced-motion`` — falls back to
 * instant scroll for users who've requested it.
 */
import { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';

const THRESHOLD_PX = 600;

const BackToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > THRESHOLD_PX);
    // Run once on mount in case the page is already deep
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const click = () => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: reduce ? 'auto' : 'smooth',
    });
  };

  return (
    <button
      type="button"
      onClick={click}
      aria-label="Back to top"
      title="Back to top"
      data-testid="back-to-top-btn"
      className={`fixed bottom-24 left-6 z-40 w-11 h-11 rounded-full
                  bg-slate-900/80 hover:bg-[#d4af37] hover:text-slate-950
                  text-[#d4af37] border border-[#d4af37]/40
                  backdrop-blur-sm shadow-lg
                  flex items-center justify-center
                  transition-all duration-200
                  ${visible
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 translate-y-2 pointer-events-none'}`}
    >
      <ArrowUp size={18} />
    </button>
  );
};

export default BackToTopButton;
